import { NgClass } from '@angular/common'
import { Component, computed, DestroyRef, effect, inject, input, output } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormControl, FormGroup } from '@angular/forms'
import { AuthService } from '@app/modules/auth/services/auth.service'
import { PersonaService } from '@app/v2/personas/services/persona.service'
import { Persona, PersonaClonedPayload } from '@app/v2/personas/types/persona.type'
import { BannerUploadFieldComponent } from '@app/v2/shared/components/banner-upload-field/banner-upload-field.component'
import { DropdownComponent } from '@app/v2/shared/components/dropdown/dropdown.component'
import { DropdownOption } from '@app/v2/shared/components/dropdown/types/dropdown-option.type'
import { WizardService } from '@app/v2/shared/components/wizard/services/wizard.service'
import { TruncationDetectorDirective } from '@app/v2/shared/directives/truncation-detector.directive'
import { Alert } from '@app/v2/shared/enums/alert.enum'
import { AlertService } from '@app/v2/shared/services/alert.service'
import { AppService } from '@app/v2/shared/services/app.service'
import { ModalService } from '@app/v2/shared/services/modal.service'
import { NgxTippyModule } from 'ngx-tippy-wrapper'
import { take } from 'rxjs'
import { EventWizardComponent } from '../../event-wizard/event-wizard.component'
import { EventTimelineSyncService } from '../../services/event-timeline-sync.service'
import { EventService } from '../../services/event.service'
import { EventCardCoreInput, EventCardPersonaAction, EventCardPersonasInput } from '../../types/event-wrapper-model.type'
import { Event } from '../../types/event.type'
import { downloadEventIdentifiersFile } from '../../utils/event-identifiers-file.utils'
import { ArchivedItemsComponent } from '../archived-items/archived-items.component'
import { EventPersonasComponent } from '../event-personas/event-personas.component'
import { EventTrackingComponent } from '../event-tracking/event-tracking.component'

@Component({
  selector: 'app-event-card',
  standalone: true,
  imports: [BannerUploadFieldComponent, DropdownComponent, EventTrackingComponent, EventPersonasComponent, TruncationDetectorDirective, NgxTippyModule, NgClass],
  templateUrl: './event-card.component.html',
  styleUrl: './event-card.component.scss'
})
export class EventCardComponent {
  public readonly core = input.required<EventCardCoreInput>()
  public readonly personasState = input<EventCardPersonasInput>({
    personas: [],
    personasTotal: null,
    personasLoading: false,
    createPersonaPending: false,
    createPersonaApiError: null
  })
  public readonly event = computed(() => this.core().event)
  public readonly events = computed(() => this.core().events)
  public readonly archived = computed(() => this.core().archived)
  public readonly canModifyEventOrPersonas = computed(() => this.core().canModifyEventOrPersonas)
  public readonly isInteractionReadonly = computed(() => this.archived() || !this.canModifyEventOrPersonas())
  public readonly canCreatePersona = computed(() => this.archived() || this.canModifyEventOrPersonas())
  public readonly canViewPersonasList = computed(() => this.canCreatePersona())
  public readonly canEdit = computed(() => this.core().canEdit)
  public readonly canCreate = computed(() => this.core().canCreate)
  public readonly loadingCounts = computed(() => this.core().loadingCounts)
  public readonly isLoading = computed(() => this.core().isLoading)
  public readonly timelineBannerLayout = computed(() => this.core().timelineBannerLayout)
  public readonly eventForm = computed(() => this.core().eventForm)
  public readonly personas = computed(() => this.personasState().personas)
  public readonly personasTotal = computed(() => this.personasState().personasTotal)
  public readonly personasLoading = computed(() => this.personasState().personasLoading)
  public readonly createPersonaPending = computed(() => this.personasState().createPersonaPending)
  public readonly createPersonaApiError = computed(() => this.personasState().createPersonaApiError)

  public readonly dropdownOptions = input<DropdownOption[]>([])
  public readonly eventsChanged = output<Event[]>()
  public readonly modalStateChanged = output<boolean>()
  public readonly archiveUpdateChanged = output<boolean>()
  public readonly bannerChanged = output<{ field: string; value: string; original_image?: string; event: Event }>()
  public readonly personaAction = output<EventCardPersonaAction>()
  public readonly restoreConfirmed = output<void>()

  public readonly visibleEventMenuOptions = computed((): DropdownOption[] => this.dropdownOptions())
  public readonly eventMenuDropdownHeight = computed((): number => this.visibleEventMenuOptions().length * 49)

  public readonly cloneModalId = computed(() => `clone-event-modal-${this.event().id}`)
  public readonly archiveModalId = computed(() => `archive-event-modal-${this.event().id}`)
  public readonly restoreModalId = computed(() => `restore-event-modal-${this.event().id}`)
  public readonly deletePermanentlyModalId = computed(() => `delete-event-permanently-modal-${this.event().id}`)
  public readonly coverPageControl = computed(() => this.eventForm().get('cover_page') as FormControl<string>)
  public readonly locationLabel = computed(() => {
    const locationDetails = this.event()?.location_details
    const venueName = locationDetails?.venue_name?.trim()
    const venueCity = locationDetails?.venue_city?.trim()
    const venueCountry = locationDetails?.venue_country?.trim() || this.event().country?.trim()

    const locationParts = [venueName, venueCity, venueCountry].filter((part): part is string => Boolean(part))
    if (locationParts.length === 0) return ''
    return locationParts.join(', ')
  })

  public readonly timezoneLabel = computed(() => {
    const locationDetails = this.event()?.location_details
    const timezone = locationDetails?.timezone?.trim() || this.event().timezone?.trim()
    const gmtOffset = timezone ? this._getGmtOffset(timezone) : ''
    if (!gmtOffset) return ''
    return `(${gmtOffset})`
  })

  private readonly _modalService = inject(ModalService)
  private readonly _wizardService = inject(WizardService)
  private readonly _appService = inject(AppService)
  private readonly _eventService = inject(EventService)
  private readonly _eventTimelineSyncService = inject(EventTimelineSyncService)
  private readonly _authService = inject(AuthService)
  private readonly _personaService = inject(PersonaService)
  private readonly _alertService = inject(AlertService)
  private readonly _destroyRef = inject(DestroyRef)
  private _lastBannerMismatchSignature: string | null = null

  constructor() {
    effect(() => {
      const event = this.event()
      const controlValue = String(this.coverPageControl()?.value ?? '').trim()
      const coverPage = String(event.cover_page ?? '').trim()
      if (controlValue === coverPage) {
        this._lastBannerMismatchSignature = null
        return
      }
      const signature = `${event.id}:${coverPage}:${controlValue}`
      if (signature === this._lastBannerMismatchSignature) return
      this._lastBannerMismatchSignature = signature
    })
  }

  /**
   * Emit the events changed event
   * @param events {Event[]} - The events
   */
  public onEventsChanged(events: Event[]): void {
    this.eventsChanged.emit(events)
  }

  /**
   * Emit the modal state changed event
   * @param isOpen {boolean} - The modal state
   */
  public onModalStateChanged(isOpen: boolean): void {
    this.modalStateChanged.emit(isOpen)
  }

  /**
   * Emit the archive update changed event
   * @param isArchiveUpdate {boolean} - The archive update state
   */
  public onArchiveUpdateChanged(isArchiveUpdate: boolean): void {
    this.archiveUpdateChanged.emit(isArchiveUpdate)
  }

  /**
   * Emit the banner changed event (includes `event` for timeline PATCH).
   * @param change { { field: string; value: string; original_image?: string } } - The banner change
   */
  public onBannerChanged(change: { field: string; value: string; original_image?: string }): void {
    const control = this.eventForm().get(change.field)
    const clearingBanner = change.field === 'cover_page' && String(change.value ?? '').trim() === ''
    if (control && control.invalid && !clearingBanner) return
    this.bannerChanged.emit({ ...change, event: this.event() })
  }

  /**
   * Emits create persona request for current event.
   * @param name {string} - The persona name.
   */
  public onCreatePersonaSubmitted(name: string): void {
    this.personaAction.emit({ type: 'createPersonaSubmitted', payload: { event: this.event(), name } })
  }

  /**
   * Emits persona name commit request for current event.
   * @param persona {Persona} - Persona with updated name.
   */
  public onPersonaNameUpdate(persona: Persona): void {
    this.personaAction.emit({ type: 'personaNameUpdate', payload: { event: this.event(), persona } })
  }

  /**
   * Emits persona landing-page click for current event.
   * @param persona {Persona} - The selected persona.
   */
  public onPersonaLandingPageClick(persona: Persona): void {
    this.personaAction.emit({ type: 'personaLandingPageClick', payload: { event: this.event(), persona } })
  }

  /**
   * Emits persona "view registrants" click for current event.
   * @param persona {Persona} - The selected persona.
   */
  public onPersonaViewRegistrantsClick(persona: Persona): void {
    this.personaAction.emit({ type: 'personaViewRegistrantsClick', payload: { event: this.event(), persona } })
  }

  /**
   * Emits a successfully cloned persona for the current event.
   * @param payload {PersonaClonedPayload} - Cloned persona and API message
   */
  public onPersonaCloned(payload: PersonaClonedPayload): void {
    this.personaAction.emit({ type: 'personaCloned', payload: { event: this.event(), ...payload } })
  }

  /**
   * Emits persona archive click for current event.
   * @param persona {Persona} - The selected persona.
   */
  public onPersonaArchiveClick(payload: { persona: Persona; personaIdsToArchive: number[] }): void {
    this.personaAction.emit({ type: 'personaArchiveClick', payload: { event: this.event(), ...payload } })
  }

  /**
   * Forwards legacy persona editor close so the timeline can refresh personas.
   */
  public onLegacyPersonaEditorClosed(payload: { event: Event }): void {
    this.personaAction.emit({ type: 'legacyPersonaEditorClosed', payload })
  }

  /**
   * Forwards personasListingWizardClosed so the timeline can refresh personas.
   */
  public onPersonasListingWizardClosed(payload: { event: Event }): void {
    this.personaAction.emit({ type: 'personasListingWizardClosed', payload })
  }

  /**
   * Navigates to the edit event wizard
   */
  public navigateToEdit(): void {
    if (this.archived() || !this.canEdit() || !this.canModifyEventOrPersonas()) return
    const eventId = this.event().id
    const result = this._wizardService.open({
      component: EventWizardComponent,
      componentInputs: {
        eventId,
        industrySectors: this._appService.industrySectors
      },
      headerConfig: {
        breadcrumbs: [this.event().name]
      }
    })
    result.onClose.pipe(take(1)).subscribe(() => {
      this._eventService.requestTimelineRefresh({ type: 'event', eventId })
    })
  }

  /**
   * Handles the event menu option click
   * @param option {DropdownOption} - The dropdown option
   */
  public onEventMenuOption(option: DropdownOption): void {
    if (option.key === 'event-identifiers') this.downloadEventIdentifiers()
    else if (option.key === 'edit') this.navigateToEdit()
    else if (option.key === 'clone') this.openCloneModal()
    else if (option.key === 'archived-items') this.openArchivedItems()
    else if (option.key === 'archive') this.openArchiveModal()
    else if (option.key === 'restore') this.openRestoreModal()
    else if (option.key === 'delete-permanently') this.openDeletePermanentlyModal()
  }

  /**
   * Downloads a text file with the workspace, event, and persona identifiers.
   */
  public downloadEventIdentifiers(): void {
    const workspace = this._authService.currentWorkspace
    if (!workspace?.name || workspace.id == null) {
      this._alertService.alert(Alert.ERROR, 'Could not download identifiers. Missing workspace details.')
      return
    }

    downloadEventIdentifiersFile({
      event: this.event(),
      workspace: {
        name: workspace.name,
        id: workspace.id,
        slug: workspace.slug ?? ''
      },
      personaService: this._personaService,
      appService: this._appService
    })
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        error: () => this._alertService.alert(Alert.ERROR, 'Failed to download event identifiers.')
      })
  }

  /**
   * Opens the archived items wizard for the current event.
   */
  public openArchivedItems(): void {
    this._wizardService.open({
      component: ArchivedItemsComponent,
      componentInputs: {
        eventId: this.event().id,
        organizationId: this.event().workspace_id,
        eventName: this.event().name,
        eventTimelineSync: this._eventTimelineSyncService
      },
      headerConfig: {
        breadcrumbs: [this.event().name, 'Archive']
      }
    })
  }

  /**
   * Opens the same clone modal as {@link EventHeaderComponent} (shared modal id).
   */
  public openCloneModal(): void {
    this._modalService.toggleModal(this.cloneModalId())
    this.modalStateChanged.emit(true)
  }

  /**
   * Opens the same archive modal as {@link EventHeaderComponent} (shared modal id).
   */
  public openArchiveModal(): void {
    this._modalService.toggleModal(this.archiveModalId())
    this.modalStateChanged.emit(true)
  }

  /**
   * Opens the same restore modal as {@link EventHeaderComponent} (shared modal id).
   */
  public openRestoreModal(): void {
    this._modalService.toggleModal(this.restoreModalId())
    this.modalStateChanged.emit(true)
  }

  /**
   * Opens the same delete permanently modal as {@link EventHeaderComponent} (shared modal id).
   */
  public openDeletePermanentlyModal(): void {
    this._modalService.toggleModal(this.deletePermanentlyModalId())
    this.modalStateChanged.emit(true)
  }

  /**
   * Gets the GMT offset for a timezone
   * @param timezone {string} - The timezone
   * @returns {string} - The GMT offset
   */
  private _getGmtOffset(timezone: string): string {
    try {
      const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        timeZoneName: 'shortOffset'
      }).formatToParts(new Date())
      const offsetValue = parts.find((part) => part.type === 'timeZoneName')?.value ?? ''
      return offsetValue.startsWith('GMT') ? offsetValue : ''
    } catch {
      return ''
    }
  }
}
